import { FlexBox } from '@dixa/lib-shared-habla';
import { produce } from 'immer';
import {
  memo,
  useCallback,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { v4 as uuid } from 'uuid';

import { CustomFieldSelectAttributeOption } from 'apis/customFields';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';

import { OptionUserInputEvent } from '../../optionsFormField.types';
import { optionsKey } from '../../optionsKey';
import { Option } from '../option/option';

type OptionsProps = {
  /** The name of the parent. */
  parentName?: string;
  /** The id of the parent option. */
  parentId?: CustomFieldSelectAttributeOption['parent_id'];
};

/** Represents the field for managing options of a custom select attribute. */
export const Options = memo(function Options(props: OptionsProps): JSX.Element {
  const { parentName = '', parentId = null } = props;
  // TODO: [TEAMS-13693] add `AttributeFormFieldValuesHavingOptions` generic back after updating to RHF v8
  const { control, setFocus, getValues } = useFormContext();
  const [dragDragEvent, setDragDragEvent] = useState<DragMoveEvent>();
  const [changedValuesByUser, setChangedValuesByUser] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    {},
  );

  const optionsFormName = parentName
    ? (`${parentName}.${optionsKey}` as const)
    : (`${optionsKey}` as const);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: optionsFormName,
    rules: parentName
      ? undefined
      : {
          validate: {
            validOptions: (options) => {
              return (
                ((options ?? []) as CustomFieldSelectAttributeOption[]).some(
                  ({ label, value }) => !!label && !!value,
                ) || 'At least one option should be valid'
              );
            },
            activeOptions: (options) => {
              const validOptions = (
                (options ?? []) as CustomFieldSelectAttributeOption[]
              ).filter(({ label, value }) => label && value);
              return (
                (validOptions.length > 0 &&
                  validOptions.some(({ is_deactivated }) => !is_deactivated)) ||
                'At least one option should be active'
              );
            },
          },
        },
  });

  const maxOrderIndex = useRef(
    // TODO: [TEAMS-13693] remove casting after updating to RHF v8
    (fields as CustomFieldSelectAttributeOption[]).reduce(
      (max, { order_index }) => (order_index > max ? order_index : max),
      0,
    ),
  );

  /**
   * Represents the sorted options by order index. Backend does not sort by
   * order index when returning, so we have to perform our own sorting.
   */
  const orderedOptions = useMemo(
    () =>
      // TODO: [TEAMS-13693] remove casting after updating to RHF v8
      ([...fields] as CustomFieldSelectAttributeOption[]).sort(
        getOrderIndexDiff,
      ),
    [fields],
  );

  const fieldIdIndexMap = useMemo(
    () => new Map(fields.map(({ id }, index) => [id, index])),
    [fields],
  );

  const isParentDeactivated = !!useWatch({
    control,
    name: `${parentName}.is_deactivated` as const,
  });

  const addNewOption = useCallback(() => {
    maxOrderIndex.current += 2;
    append(
      {
        id: uuid(),
        label: '',
        value: '',
        is_deactivated: isParentDeactivated,
        order_index: maxOrderIndex.current,
        parent_id: parentId,
      },
      { shouldFocus: false },
    );
  }, [append, isParentDeactivated, parentId]);

  const getNewOrderIndex = (dragIndex: number, hoverIndex: number): number => {
    return (
      (orderedOptions[hoverIndex].order_index ?? 0) +
      (dragIndex < hoverIndex ? 1 : -1)
    );
  };

  /** Handles the event for when an option is moved. */
  const handleOptionMove = useCallback((event: DragMoveEvent) => {
    setDragDragEvent(event);
  }, []);

  /** Handles the event for when an option is removed. */
  const handleOptionRemove = useCallback(
    (index) => {
      remove(index);
      setChangedValuesByUser({ [index]: false });
    },
    [remove],
  );

  /** Handles the event for when an option receives user input. */
  const handleOptionUserInput = useCallback(
    ({ index, field }: OptionUserInputEvent) => {
      const isLastOption = fields.length - 1 === index;

      if (isLastOption) {
        addNewOption();
      }

      if (field === 'value') {
        setChangedValuesByUser({ [index]: true });
      }
    },
    [addNewOption, fields.length],
  );

  // Handles focusing the first option when new nested options are created.
  useEffectOnce(() => {
    if (
      parentName &&
      fields.length === 1 &&
      (fields[0] as CustomFieldSelectAttributeOption).label === '' &&
      (fields[0] as CustomFieldSelectAttributeOption).value === ''
    ) {
      setFocus(`${optionsFormName}.0.label` as const);
    }
  });

  // Handles appending a blank option to the list.
  useEffectOnce(() => {
    if (
      fields.length === 1 ||
      ((
        fields[fields.length - 1] as
          | CustomFieldSelectAttributeOption
          | undefined
      )?.label !== '' &&
        (
          fields[fields.length - 1] as
            | CustomFieldSelectAttributeOption
            | undefined
        )?.value !== '')
    ) {
      addNewOption();
    }
  });

  // Handles moving the fields when drag and drop event changes. We need to
  // run this in an effect because React Hook Form FieldArray actions need to be
  // triggered per render. See https://react-hook-form.com/api/usefieldarray
  useUpdateEffect(() => {
    if (dragDragEvent && orderedOptions) {
      const { dragIndex, hoverIndex } = dragDragEvent;
      const fieldDragIndex =
        fieldIdIndexMap.get(orderedOptions[dragIndex].id) ?? -1;
      const newOrderIndex = getNewOrderIndex(dragIndex, hoverIndex);
      const options = getValues(optionsFormName);

      // TODO: [TEAMS-13693] remove casting after updating to RHF v8
      const tempOrderedOptions = produce(
        fields as CustomFieldSelectAttributeOption[],
        (draft) => {
          draft[fieldDragIndex] = {
            ...draft[fieldDragIndex],
            order_index: newOrderIndex,
          };
          draft.sort(getOrderIndexDiff);
        },
      );
      for (let i = 0; i < tempOrderedOptions.length; i++) {
        const currentFieldIndex =
          fieldIdIndexMap.get(tempOrderedOptions[i].id) ?? -1;

        update(currentFieldIndex, {
          ...options[currentFieldIndex],
          order_index: i * 2,
        });
      }
    }
  }, [dragDragEvent]);

  return (
    <FlexBox flexDirection="column" width="100%">
      {orderedOptions.map((option, index) => (
        <Option
          key={option.id}
          parentName={parentName}
          fieldIndex={fieldIdIndexMap.get(option.id) ?? -1}
          orderIndex={index}
          isLastOption={fields.length - 1 === fieldIdIndexMap.get(option.id)}
          isValueChangedByUser={
            changedValuesByUser[fieldIdIndexMap.get(option.id) ?? -1]
          }
          isParentDeactivated={!!isParentDeactivated}
          onRemove={handleOptionRemove}
          onUserInput={handleOptionUserInput}
          onMove={handleOptionMove}
        />
      ))}
    </FlexBox>
  );
});

/**
 * Returns the difference between the order indexes of given options.
 *
 * @param a First option
 * @param b Second option
 */
const getOrderIndexDiff = (
  a: CustomFieldSelectAttributeOption,
  b: CustomFieldSelectAttributeOption,
) => a.order_index - b.order_index;
